import type { EliteOnboardingStatusResponse } from '../../types/misc.types'
import { buildFamilyOnboardingViewProps } from '../new-elite-landing/pages/family/familyOnboardingViewProps'
import { ELITE_PLANS_ELITE_PRIME_TAB_ID } from './constants/planIds'
import { resolveElitePrimeUpgradeDetails } from './elitePlansUpgradeDetails'

/** `throughFamilyAccess` — includes purchase and primary-not-Elite blocked. */
export function resolveIsFamilyMode(status?: EliteOnboardingStatusResponse): boolean {
  return buildFamilyOnboardingViewProps(status).isFamilyMode
}

/** Primary is Elite / Prime buying for a family member (not the blocked upgrade-primary state). */
export function resolveIsFamilyPurchase(status?: EliteOnboardingStatusResponse): boolean {
  const familyConfig = buildFamilyOnboardingViewProps(status)
  return familyConfig.isFamilyMode && !familyConfig.isEliteBlockedBecausePrimaryNotElite
}

/** Primary is not Elite / Prime — secondary cannot buy until primary upgrades. */
export function resolveIsFamilyBlocked(status?: EliteOnboardingStatusResponse): boolean {
  return buildFamilyOnboardingViewProps(status).isEliteBlockedBecausePrimaryNotElite
}

/**
 * Prime tab on family surfaces: primary is free (not Elite) so they cannot buy Prime for secondary.
 * Covers `FAMILY_ONBOARDING_NOT_ALLOWED` and primary-free + secondary-Elite.
 */
export function resolveIsFamilyPrimePurchaseBlocked(
  status?: EliteOnboardingStatusResponse
): boolean {
  if (!resolveIsFamilyMode(status)) return false
  if (resolveIsFamilyBlocked(status)) return true
  return status?.isPrimaryUserElite === false
}

/** Primary Elite buying Elite for a free secondary (family purchase body on Elite tab). */
export function resolveIsFamilyPrimaryEliteSecondaryFree(
  status?: EliteOnboardingStatusResponse
): boolean {
  if (!resolveIsFamilyPurchase(status)) return false
  return status?.isPrimaryUserElite === true && status?.isSecondaryUserElite === false
}

/** Primary and secondary are both Elite (family member dashboard + Prime sell/upgrade). */
export function resolveIsFamilyBothElite(status?: EliteOnboardingStatusResponse): boolean {
  if (!resolveIsFamilyMode(status)) return false
  return status?.isPrimaryUserElite === true && status?.isSecondaryUserElite === true
}

/** Secondary already has Elite — Active Elite dashboard on Elite-locked family landing. */
export function resolveIsFamilySecondaryElite(status?: EliteOnboardingStatusResponse): boolean {
  return resolveIsFamilyMode(status) && status?.isSecondaryUserElite === true
}

/**
 * Secondary already has Elite Prime — Active Prime dashboard, not purchase.
 * `isUserElitePrime` is the subject’s membership; upgrade details ⇒ Elite-only, not yet Prime.
 */
export function resolveIsFamilySecondaryPrime(status?: EliteOnboardingStatusResponse): boolean {
  return (
    resolveIsFamilyMode(status) &&
    resolveIsUserElitePrime(status) &&
    status?.isSecondaryUserElite === true &&
    !resolveElitePrimeUpgradeDetails(status)
  )
}

/**
 * Family Prime purchase for a free secondary (FamilyPurchaseBody).
 *
 * Secondary already Elite (not Prime) → SellBody upgrade, not FamilyPurchaseBody.
 * Secondary already Prime → Active Prime dashboard (`resolveIsFamilySecondaryPrime`).
 * Do not use `isPrimeConsent` here — that only drives the consent API before CTA.
 * Do not use `isUserElitePrime` as “primary is Prime” — that flag is the subject’s membership.
 */
export function resolveIsFamilyPrimePurchaseForSecondary(
  status?: EliteOnboardingStatusResponse
): boolean {
  if (!resolveIsFamilyPurchase(status)) return false
  // Elite secondary upgrades via SellBody; only free secondary uses family Continue
  if (status?.isSecondaryUserElite === true) return false
  return !resolveIsFamilySecondaryPrime(status)
}

/** Elite membership from elite insights only. */
export function resolveIsUserElite(status?: EliteOnboardingStatusResponse): boolean {
  return Boolean(status?.eliteInsights?.userElite)
}

/** Elite Prime membership from top-level `isUserElitePrime` on prime-status. */
export function resolveIsUserElitePrime(status?: EliteOnboardingStatusResponse): boolean {
  return status?.isUserElitePrime ?? false
}

/**
 * Advice-card lock for the active plan tab.
 * Elite and Elite Prime are evaluated separately — never combined.
 */
export function resolveShowAdviceCardLock(
  planId: string,
  status?: EliteOnboardingStatusResponse
): boolean {
  if (planId === ELITE_PLANS_ELITE_PRIME_TAB_ID) {
    return !resolveIsUserElitePrime(status)
  }
  return !resolveIsUserElite(status)
}
